import { isPathActive } from "./isPathActive";
import type { BuildNavItem } from "./types";

/**
 * Whether any of a Build item's sub-items matches the current path. This is what
 * makes an expandable group read as active (the group has no destination of its
 * own — it's active when one of its children is). A group's `activePaths` also
 * count, for routes that belong to the group but match none of its children.
 */
export const isBuildItemChildActive = (
  item: BuildNavItem,
  pathname: string,
): boolean =>
  (item.children ?? []).some(child =>
    isPathActive({ exact: child.exact, pathname, to: child.to }),
  ) || (item.activePaths ?? []).some(to => isPathActive({ pathname, to }));

/**
 * The single source of truth for "does the current path activate this Build
 * item?". Every dock surface (expanded rows/groups, collapsed grid menu, and
 * the active-section detection) routes through this so they can't drift apart.
 *
 * - A group (has children) is active when one of its children matches.
 * - A leaf is active when its own `path` matches, or one of its `activePaths`
 *   matches — extra routes that belong to the item but live elsewhere (e.g. the
 *   Settings leaf lighting up on Settings-page routes under other prefixes).
 */
export const isBuildItemActive = (
  item: BuildNavItem,
  pathname: string,
): boolean => {
  if ((item.children ?? []).length > 0) {
    return isBuildItemChildActive(item, pathname);
  }

  return (
    isPathActive({ exact: item.exact, pathname, to: item.path }) ||
    (item.activePaths ?? []).some(to => isPathActive({ pathname, to }))
  );
};
